<template src="./ActiveUsers.template.html"></template>

<script setup>
import { computed, onMounted, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useUserStore } from '../stores/user'
import { Users } from 'lucide-vue-next'
import ErrorDisplay from '../components/ui/ErrorDisplay.vue'
import PageHeader from '../components/ui/PageHeader.vue'
import LayoutMain from '../components/ui/LayoutMain.vue'
import ExploringView from '../components/views/ExploringView.vue'
import EveryoneView from '../components/views/EveryoneView.vue'

// Stores and router
const router = useRouter()
const { t } = useI18n()
const authStore = useAuthStore()
const userStore = useUserStore()

// Reactive state
const loadingExploring = ref(false)

// Computed properties
const isAuthenticated = computed(() => authStore.isAuthenticated)
const activeUsers = computed(() => userStore.activeUsers)

const headerSubtitle = computed(() => {
  return `${activeUsers.value.length} ${t('activeUsers.exploringNow')}`
})

const activeUserIds = computed(() => activeUsers.value.map(u => u.id))

// Data loading
async function loadActiveUsers() {
  if (!isAuthenticated.value || !authStore.user || !userStore.profile) {
    return
  }

  try {
    loadingExploring.value = true
    await userStore.fetchActiveUsers()
  } catch (error) {
    console.error('Failed to fetch active users:', error)
  } finally {
    loadingExploring.value = false
  }
}

// Event handlers
async function handleToggleLike(userId) {
  try {
    await userStore.toggleLike(userId)
  } catch (error) {
    console.error('Failed to toggle like:', error)
  }
}

function handleViewProfile(userId) {
  router.push(`/user/${userId}`)
}

function handleStartExploring() {
  router.push('/spark')
}

// Lifecycle hooks
onMounted(async () => {
  await loadActiveUsers()
})

// Watchers
watch(
  isAuthenticated,
  async newValue => {
    if (newValue) {
      setTimeout(async () => {
        await loadActiveUsers()
      }, 100)
    }
  },
  { immediate: true }
)

watch(
  () => userStore.profile,
  async newProfile => {
    if (newProfile && isAuthenticated.value && authStore.user) {
      await loadActiveUsers()
    }
  },
  { immediate: true }
)
</script>
